/**
 * Envio de mensagem (C22, §10.2).
 *
 * A resposta devolve o `id` CANÔNICO da mensagem (§3.2) — nunca o do
 * Chatwoot. `estado` nasce `enviada`: os estados de entrega
 * (delivered/read/failed/unsupported) são a C23, e prometê-los aqui antes
 * de existirem seria contrato mentindo.
 */

import { z } from 'zod';
import { AnexoSchema } from './anexos.js';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

export const EnviarMensagemSchema = z
  .object({
    /**
     * Pode ser vazio QUANDO há anexo.
     *
     * ── Por que a exigência saiu daqui ──────────────────────────────────
     *
     * "Mensagem vazia não é envio" continua valendo — mudou o lugar onde ela é
     * decidida. Os anexos viajam como arquivo no `multipart`, e não neste
     * corpo: a schema não tem como saber se existem, e exigir texto aqui
     * recusava o envio de só uma foto ou um áudio — que é uma mensagem
     * completa em qualquer aplicativo de conversa.
     *
     * A conferência foi para `enviar-mensagem.ts`, onde texto e anexo são
     * conhecidos ao mesmo tempo. Ela recusa exatamente o mesmo caso de antes:
     * nada escrito e nada anexado.
     */
    conteudo: z.string().max(150_000),
    /** Nota interna do atendente — nunca sai pelo canal do cliente (A02 §12). */
    privada: z.boolean().optional().default(false),
    /**
     * Quem a nota interna chama com "@" (#569): os ids das pessoas escolhidas
     * na lista. Cada uma recebe um aviso com o trecho da nota. Só vale com
     * `privada: true` — na mensagem ao cliente, o "@" não chama ninguém, para
     * nome de colega não vazar por engano.
     *
     * O texto continua como a pessoa escreveu ("@Maria Souza pode aprovar?"):
     * o servidor é quem transforma o "@Nome" em menção.
     */
    mencoes: z.array(IdSchema).max(20).optional(),
    /**
     * Nome do template aprovado — obrigatório quando o canal exige template
     * fora da janela (§8.2).
     *
     * ── Com template, o `conteudo` enviado é IGNORADO ────────────────────
     *
     * O que o cliente recebe é o texto que a META aprovou, montado por ela a
     * partir do nome e dos valores. O que o atendente digita nunca sai — e
     * gravar aquilo no histórico faria a conversa mostrar uma mensagem que o
     * cliente não recebeu.
     *
     * Então o servidor monta o conteúdo a partir do corpo do template e de
     * `templateValores`, e é esse texto que vai para o histórico. É a única
     * forma de o registro dizer a verdade.
     */
    templateNome: z.string().min(1).max(200).optional(),
    /**
     * Um valor por variável do corpo, na ordem: o primeiro é `{{1}}`.
     *
     * A Meta recusa envio com contagem diferente da que ela aprovou, e o erro
     * dela fala em número — nunca em qual faltou. O servidor confere contra o
     * catálogo antes de gastar a chamada.
     */
    templateValores: z.array(z.string().max(1000)).max(20).optional(),
  })
  .strict();

export type EnviarMensagem = z.infer<typeof EnviarMensagemSchema>;

export const MensagemEnviadaSchema = z.object({
  /** Canônico (§5.1), cunhado em core.source_links — nunca o id do Chatwoot. */
  id: IdSchema,
  /** O canônico da conversa, ecoado — o mesmo do caminho da requisição. */
  conversaId: IdSchema,
  estado: z.literal('enviada'),
  privada: z.boolean(),
  criadaEm: InstanteSchema,
  /**
   * Os anexos, quando houve (C27). Ausente quando a mensagem é só texto.
   *
   * A URL é assinada por nós e expira — nunca a `data_url` do motor, que a
   * A02 §4.2 mediu como pública e permanente. `expiraEm` viaja junto para a
   * tela poder avisar, em vez de mostrar um link que morre calado.
   */
  anexos: z.array(AnexoSchema).optional(),
});

export type MensagemEnviadaResposta = z.infer<typeof MensagemEnviadaSchema>;
